/**
 * A decoupled seam between the map app (map/next, valtio-based, lazy-loaded)
 * and the AssistantWidget (mounted at the app root, outside the map's provider).
 *
 * The map registers a snapshot getter + action handlers on mount and clears
 * them on unmount. The widget pulls a snapshot at send time and dispatches
 * view actions — without importing anything from map/next, so the root bundle
 * stays free of the (large) map app.
 */

import { proxy } from 'valtio';

/**
 * Shared layout state between the assistant widget and the map's left column,
 * so the two share the column without overlapping and without a feedback loop:
 *
 * - `assistantReserve` (widget → tree): a FIXED amount of space the layer tree
 *   keeps clear at the bottom when the assistant is docked, guaranteeing the
 *   panel a minimum even when the tree is very tall. 0 when not docked.
 * - `layerTreeBottom` (tree → widget): the tree card's bottom edge in VIEWPORT
 *   coordinates (via getBoundingClientRect), so the panel sits just beneath the
 *   tree regardless of any top nav offsetting the map area.
 */
/**
 * Assistant UI state mirrored outside Redux. Map routes run under a SEPARATE
 * Redux store (see map-layout.tsx), so anything rendered inside the map (e.g.
 * the inline launcher) can't read the root store's assistant slice. The widget
 * (root store) keeps this in sync and registers an opener; the launcher reads
 * and calls these instead of Redux.
 */
/** Default docked width. Also the floor/ceiling for user resizing. */
export const ASSISTANT_WIDTH_DEFAULT = 360;
export const ASSISTANT_WIDTH_MIN = 300;
/** Past ~2/3 of the viewport the app it sits beside stops being usable. */
export const ASSISTANT_WIDTH_MAX = 720;
const WIDTH_STORAGE_KEY = 'birdi-assistant-width';

export function clampAssistantWidth(px: number): number {
  if (!Number.isFinite(px)) return ASSISTANT_WIDTH_DEFAULT;
  const ceiling = Math.min(
    ASSISTANT_WIDTH_MAX,
    // Never wider than 2/3 of the window, so a saved width from a big monitor
    // can't swallow a laptop screen on the next visit.
    typeof window !== 'undefined' && window.innerWidth
      ? Math.round(window.innerWidth * 0.66)
      : ASSISTANT_WIDTH_MAX,
  );
  return Math.round(
    Math.min(Math.max(px, ASSISTANT_WIDTH_MIN), Math.max(ceiling, ASSISTANT_WIDTH_MIN)),
  );
}

function loadStoredWidth(): number {
  try {
    const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY);
    if (raw) return clampAssistantWidth(Number(raw));
  } catch {
    // Private mode / storage disabled — the default is fine.
  }
  return ASSISTANT_WIDTH_DEFAULT;
}

export const assistantUi = proxy<{
  open: boolean;
  unread: boolean;
  enabled: boolean;
  suppressed: boolean;
  width: number;
}>({
  open: false,
  /** A response finished while the panel was minimised (launcher shows a dot). */
  unread: false,
  /**
   * Docked width in px, user-resizable. Lives here rather than in Redux so the
   * panel, the RootLayout padding that reserves its column, and the map (which
   * runs its own store) all read one value — they have to agree or the app
   * either overlaps the panel or leaves a gap beside it.
   */
  width: typeof window === 'undefined' ? ASSISTANT_WIDTH_DEFAULT : loadStoredWidth(),
  /**
   * The org's plan includes the ai-assistant feature (mirrored from the root
   * store's meta by AssistantWidget). Entry points that can't read Redux (the
   * launcher under the map's separate store) hide themselves on this.
   */
  enabled: false,
  /**
   * The CURRENT route is one the assistant is switched off on (see
   * `isAssistantHiddenPath`). Kept beside `enabled` — and mirrored here rather
   * than read from the router — so the Redux-free entry points, which also sit
   * outside the router's context in the map's own tree, can hide on it.
   */
  suppressed: false,
});

/**
 * Routes where the assistant is switched off entirely — no launcher, no side
 * tab, no panel. Currently the Sites pages (BIR-321): the sites list and a
 * single site's own pages (its Maps and Reference sets tabs).
 *
 * Maps opened FROM a site are ordinary `/map/...` routes and keep the
 * assistant. An open panel is hidden while on Sites, not closed — the
 * conversation is still there when the user navigates back out.
 */
export function isAssistantHiddenPath(pathname: string): boolean {
  return pathname === '/sites' || pathname.startsWith('/sites/');
}

/**
 * Sets the docked width, clamped, and remembers it. Persisting is best-effort:
 * a resize that doesn't survive a reload is a nuisance, a thrown exception mid
 * drag is a broken panel.
 */
export function setAssistantWidth(px: number): void {
  const next = clampAssistantWidth(px);
  assistantUi.width = next;
  try {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(next));
  } catch {
    // Storage unavailable — keep the in-session width.
  }
}

export type DataChangeScope = 'maps' | 'map_folders';

/**
 * Bumped whenever an assistant tool mutates workspace data (rename/create/
 * duplicate/move a map, create a folder) outside of any live map — e.g. from
 * the dashboard. Root-mounted, store-agnostic components that list this data
 * (the dashboard's Latest Maps widget) subscribe via valtio and refetch when
 * their scope's count changes, since there's no map bridge to push to there.
 */
export const dataVersion = proxy<Record<DataChangeScope, number>>({
  maps: 0,
  map_folders: 0,
});

export function notifyDataChanged(scope: DataChangeScope): void {
  dataVersion[scope] += 1;
}

let assistantOpener: ((prompt?: string) => void) | null = null;

/** Called by AssistantWidget on mount; the handler dispatches on the root store. */
export function registerAssistantOpener(
  fn: ((prompt?: string) => void) | null,
): void {
  assistantOpener = fn;
}

/** Open the assistant panel from anywhere (any store, or none). */
export function requestAssistantOpen(prompt?: string): void {
  assistantOpener?.(prompt);
}

export const mapLayout = proxy<{
  assistantReserve: number;
  layerTreeBottom: number;
  /**
   * Viewport rect of the bottom-centre map switcher pill (2D/3D etc.), so the
   * docked panel can lift above it when they'd overlap. null when absent.
   */
  bottomBar: { top: number; left: number; right: number } | null;
  /**
   * True while the map app is mounted (reactive mirror of the module-level
   * `current` registration). Lets root-mounted, Redux-free UI — the side tab —
   * pick a z-index by context: below the map's toolboxes on a map, above
   * everything elsewhere. Maintained by registerMap / unregisterMap.
   */
  mapMounted: boolean;
  /**
   * True while the map is showing the FULL media view (renderer === 'media' —
   * the media browser replaces the map canvas). The side tab hides there and
   * the map-header launcher stays as the entry point (BIR-214). Maintained by
   * the map app; reset by registerMap / unregisterMap so a stale value can't
   * outlive the map that set it.
   */
  mediaViewActive: boolean;
}>({
  assistantReserve: 0,
  layerTreeBottom: 0,
  bottomBar: null,
  mapMounted: false,
  mediaViewActive: false,
});

export interface MapLayerInfo {
  id: string;
  name: string;
  type: string;
  visible: boolean;
}

/** Live, ephemeral map state — the parts that only exist in the browser. */
export interface MapSnapshot {
  mapId: string | null;
  /** The map's display name (for the composer context chip). */
  mapName?: string;
  /** True while the map/layers are still loading (queued actions wait on this). */
  loading?: boolean;
  center?: { lat: number; lng: number };
  /**
   * Visible extent [west, south, east, north]. MapLibre only — undefined in
   * the Cesium/3D renderer, so treat its absence as "no viewport scope".
   */
  bounds?: [number, number, number, number];
  zoom?: number;
  renderer?: string;
  layerCount: number;
  selectedLayerIds: string[];
  visibleLayers: MapLayerInfo[];
  hiddenLayers: MapLayerInfo[];
}

export type MapView = '2d' | '3d' | 'media' | 'split';
export type MapMeasureSystem = 'metric' | 'imperial';
/**
 * Mirrors ValidCreationTools string values (map/next/editing/controller.ts).
 * AIDetect deliberately excluded — it's not a drawing tool; startAnnotation
 * with tool: AIDetect used to silently no-op (Mode.Annotations has no
 * geometry type or rendering for it). AI Detect has its own three-surface
 * flow — see startAiDetect below.
 */
export type AnnotationTool = 'Marker' | 'Polygon' | 'Line' | 'Circle' | 'Rectangle';
/** The three distinct AI Detect entry points (editing/toolbar.tsx's AIDetectDropdown). */
export type AiDetectSurface = 'map_area' | 'orthomosaic' | 'media';
/** Mirrors the map's BaseMap union (map/next/map-state.ts). */
export type BasemapStyle =
  | 'light'
  | 'dark'
  | 'satellite'
  | 'satellite-hybrid'
  | 'outdoor'
  | 'map'
  | 'white'
  | 'black';

/** View-only actions the assistant may drive on the live map. */
export interface MapActions {
  /** Show or hide a layer (idempotent — sets, doesn't toggle). */
  setLayerVisibility: (layerId: string, visible: boolean) => void;
  /** Select a layer and, if a bbox is supplied, fly to it. */
  focusLayer: (
    layerId: string,
    bbox?: [number, number, number, number],
  ) => void;
  /** Switch the map's view mode (2D / 3D / media / split). */
  switchView: (view: MapView) => void;
  /** Set the measurement system for the current view. */
  setMeasurementSystem: (system: MapMeasureSystem) => void;
  /** Clear the current layer selection. */
  deselectAll: () => void;
  /** Filter the layer panel by name (empty string clears the filter). */
  searchLayers: (term: string) => void;
  /** Fit the camera to a bounding box [west, south, east, north]. */
  fitView: (bbox: [number, number, number, number]) => void;
  /**
   * Open the Request Processing modal with the given layers pre-selected. The
   * user reviews eligibility + cost and submits there — this only opens the
   * modal, it does not start (or pay for) a processing job.
   */
  openRequestProcessing: (layerIds: string[]) => void;
  /**
   * Generate an annotation PDF report for the given annotation/group layers.
   * Captures the live map, uploads it, and kicks off server-side generation —
   * the same flow as the layer-tree "Export annotation report (PDF)" action.
   */
  exportAnnotationReport: (layerIds: string[]) => void;
  /**
   * Open the Add-data (combined upload) modal so the user can pick files.
   * No-ops without the map:data:create permission.
   */
  openUploadModal: () => void;
  /**
   * Show the timeline view (time-series comparison). Idempotent — opens it if
   * hidden, leaves it if already shown. No-ops without the timeline-view
   * plan feature.
   */
  showTimeline: () => void;
  /**
   * Open the map canvas export panel and crop box, as the toolbar's Export
   * button does. Idempotent. No-ops without map-export, map:annotate, or
   * outside the 2D renderer.
   */
  openMapExport: () => void;
  /**
   * Show the TABLE VIEW for the given layers — the sortable table, which tables
   * media/photos, annotations and their measurements, and vector features. Not
   * annotations-only, and distinct from switchView('media') (the photo tagger).
   *
   * `layerIds` is not optional in practice: the table's columns and rows come
   * from the current SELECTION (`activeLayers[0]` decides the whole shape), so
   * showing it without selecting anything yields an empty, column-less panel
   * that reads as "it didn't open". The UI can't hit this because you reach the
   * table by clicking a layer.
   */
  showTableView: (layerIds?: string[]) => void;
  /** Activate a drawing tool; the user then draws on the map. */
  startAnnotation: (tool: AnnotationTool) => void;
  /**
   * Set the map up to measure a volume, the same way the toolbar's volume
   * button does: reuse the map's volumetric folder or create one
   * (`group` + `data.isVolumetric`), select and reveal it, then arm the
   * Polygon tool so whatever the user draws lands INSIDE that folder.
   *
   * Scout needs this because `startAnnotation('Polygon')` alone drops the
   * boundary wherever the tree happens to be selected — a volume outside a
   * volumetric folder is invisible to the flows built on the flag (annotation
   * reports, volume-vs-area classification), which is what team testing hit:
   * "it calced a volume, but didn't place the polygons into a volume
   * directory". No-ops without map:annotate.
   */
  startVolumeMeasurement: () => void;
  /**
   * Screenshot the CURRENT view and post it back so the agent can look at it
   * (BIR-145). This is the only way it can see an orthomosaic — a COG can be
   * gigabytes, so the browser's own render is the answer.
   *
   * Unlike every other action here this one has a return leg: the agent is
   * blocked waiting on `POST /v1/assistant/capture/<token>`, so failing
   * silently would strand the turn until it times out. Errors are swallowed
   * deliberately (the agent's own timeout message covers the user) but logged.
   */
  captureMapView: (token: string) => void;
  /** Switch the basemap style (satellite / street / light / dark …). */
  setBasemap: (style: BasemapStyle) => void;
  /**
   * Enable the split comparison slider with the given layers on each side
   * (2D only — switches back to 2D if needed). Null clears a side.
   */
  configureSplitCompare: (
    leftLayerId: string | null,
    rightLayerId: string | null,
  ) => void;
  /**
   * Open the Create Difference Grid panel with `layerId` (a DTM/DSM layer)
   * pre-selected as the comparison surface. Opens the panel only — the user
   * still picks the surface to compare against and confirms.
   */
  openDifferenceGrid: (layerId: string) => void;
  /**
   * Add a layer from Birdi's built-in layer library (hillshading, contours,
   * government/environmental data sets — see map-library/const.ts) to the
   * map. `libraryLayerId` matches a MapCardProps.mapId there. No-ops without
   * the map:data:create permission or the map-library plan feature.
   */
  addLibraryLayer: (libraryLayerId: string) => void;
  /**
   * Open one of Birdi's three AI Detect surfaces (see AiDetectSurface):
   * map_area arms the on-map box-draw tool, orthomosaic opens the ortho
   * picker, media switches to the photo tagger. `layerIds` only applies to
   * media (which geoimages) — ignored otherwise. No-ops without the
   * map:annotate permission or the ai-detect-media plan feature.
   */
  startAiDetect: (surface: AiDetectSurface, layerIds?: string[]) => void;
  /**
   * Move layers the way drag-and-drop in the layer panel does (BIR-262):
   * into an existing layer folder (`parentId`), into a new folder created
   * for them (`newFolderName`, placed under `parentId` or at the root), or
   * to the root (`parentId: null`), landing at the top or bottom of that
   * folder or directly above/below a named sibling (which is also how a
   * folder is reordered). Goes through the socket so the panel and other
   * collaborators update live. No-ops without map:edit.
   */
  moveLayers: (layerIds: string[], target: LayerMoveTarget) => void;
  /** Rename a layer or folder. No-ops without map:edit. */
  renameLayer: (layerId: string, name: string) => void;
}

export interface LayerMoveTarget {
  parentId: string | null;
  newFolderName?: string;
  position?: 'top' | 'bottom';
  aboveLayerId?: string;
  belowLayerId?: string;
}

interface Registration {
  getSnapshot: () => MapSnapshot | null;
  actions: MapActions;
}

let current: Registration | null = null;

/**
 * Actions requested while no map is registered (e.g. the assistant navigated
 * to a map and immediately asked for an action, but the map app is still
 * loading). Flushed on registerMap; entries expire so a stale action can't
 * fire long after the turn that queued it.
 */
const PENDING_TTL_MS = 60_000;
const PENDING_CAP = 8;
// Give slow maps time to hydrate before giving up and flushing best-effort.
const FLUSH_MAX_WAIT_MS = 30_000;
let pendingActions: { fn: (actions: MapActions) => void; expires: number }[] =
  [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

function stopFlushLoop(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

function mapReady(reg: Registration): boolean {
  try {
    return reg.getSnapshot()?.loading === false;
  } catch {
    return false;
  }
}

function flushPending(reg: Registration): void {
  const now = Date.now();
  const queued = pendingActions;
  pendingActions = [];
  for (const q of queued) {
    if (q.expires < now) continue;
    try {
      q.fn(reg.actions);
    } catch {
      // Queued action failed — drop it silently.
    }
  }
}

/**
 * Flush queued actions once the map reports loaded (layers hydrated), polling
 * briefly; after FLUSH_MAX_WAIT_MS flush best-effort anyway so actions aren't
 * silently swallowed by a very slow map.
 */
function ensureFlushLoop(reg: Registration): void {
  if (flushTimer) return;
  const deadline = Date.now() + FLUSH_MAX_WAIT_MS;
  const attempt = () => {
    if (current !== reg) {
      // Map went away; keep the queue for the next registration (TTL still
      // bounds staleness).
      stopFlushLoop();
      return;
    }
    if (pendingActions.length === 0) {
      stopFlushLoop();
      return;
    }
    if (mapReady(reg) || Date.now() > deadline) {
      stopFlushLoop();
      flushPending(reg);
    }
  };
  flushTimer = setInterval(attempt, 300);
  attempt();
}

/** Called by the map app on mount. Queued actions flush once layers load. */
export function registerMap(reg: Registration): void {
  current = reg;
  mapLayout.mapMounted = true;
  mapLayout.mediaViewActive = false;
  stopFlushLoop();
  if (pendingActions.length > 0) ensureFlushLoop(reg);
}

/** Called by the map app on unmount. No-ops if a newer map already registered. */
export function unregisterMap(reg?: Registration): void {
  if (!reg || current === reg) {
    current = null;
    mapLayout.mapMounted = false;
    mapLayout.mediaViewActive = false;
  }
}

/** Current live map state, or null when no map is open. Never throws. */
export function getMapSnapshot(): MapSnapshot | null {
  try {
    return current?.getSnapshot() ?? null;
  } catch {
    return null;
  }
}

/**
 * Runs a view action against the live map. If no map is registered yet, or
 * one is registered but its layers are still loading (e.g. right after the
 * assistant navigated to it), the action is queued and runs once the map
 * reports loaded. Returns false if the action didn't run immediately.
 */
export function runMapAction(fn: (actions: MapActions) => void): boolean {
  const reg = current;
  if (!reg || !mapReady(reg)) {
    pendingActions.push({ fn, expires: Date.now() + PENDING_TTL_MS });
    if (pendingActions.length > PENDING_CAP) pendingActions.shift();
    if (reg) ensureFlushLoop(reg);
    return false;
  }
  try {
    fn(reg.actions);
    return true;
  } catch {
    return false;
  }
}
